import { useFeatureLayer } from "./useFeatureLayer";
import { useAppSelector } from "app/hooks";
import Button from "shared/components/Button";
import { ReactComponent as CrossSVG } from "assets/icons/cross.svg";
import React, { useCallback } from "react";
import PortalledContent from "shared/components/PortalledContent";
import Panel from "shared/components/Panel";
import useMedia from "use-media";
import { MOBILE_VIEW_BREAKPOINT } from "shared/helpers/mediaQueries";
import DataItem from "shared/components/DataItem";
import { basicStringParse, LAYER_DETAILS } from "./featureLayerModels";
import { getLayerName } from "components/PanelSettings/components/Layers/layerModels";

const PORTAL_ID = "FeatureReactContainer";

const FeatureLayer: React.FC = () => {
  const largerThanMobileView = useMedia(MOBILE_VIEW_BREAKPOINT);
  const targetFeature = useFeatureLayer();
  // NOTE: `?` used as we cannot guarantee that the request feature and child
  // will exist
  const groupName = useAppSelector((state) =>
    targetFeature
      ? state.krpano.layers.features[targetFeature.featureIndex]?.name
      : null
  );
  const featureDetails = useAppSelector((state) =>
    targetFeature
      ? state.krpano.layers.features[targetFeature.featureIndex]?.children[
          targetFeature.childIndex
        ]?.details
      : null
  );

  // Close the Feature panel
  const closePanel = useCallback(() => {
    window.krpano.call("closeFeature();");
  }, []);

  const titleText = `${
    groupName ? getLayerName(groupName) : "Feature"
  } Details`;

  // NOTE: `key` prop in Fragment
  // REASON: Change to `targetFeature` was done outside of React so did not cause
  // a re-render, therefore the `key` prop is needed to force that behaviour.
  const featureLayerInner = (
    <React.Fragment
      key={`FEATURE_CONTENT_${targetFeature?.featureIndex}_${targetFeature?.childIndex}`}
    >
      <div className="KrpanoReactInner">
        {/* SEE: FEATURE_LAYER_FULLSCREEN */}
        {largerThanMobileView && (
          <Button
            colour="Theme"
            additionalClassNames="Cross NoBorder"
            onClick={closePanel}
            children={
              <CrossSVG
                height={16}
                width={16}
                role="img"
                aria-label="Close Panel"
                title="Close Panel"
              />
            }
          />
        )}
        <div className="Details DisplayFlex FlexColumn LargeGap">
          {/* SEE: FEATURE_LAYER_FULLSCREEN */}
          {largerThanMobileView && <h2>{titleText}</h2>}
          {/*
            NOTE: Scout Date comes from pano date, not when the image was taken
            We assume they were done together because there's no indication
            otherwise
          */}
          {/* <h3>Details</h3> */}
          <hr />
          <div className="MeasurementsParent DisplayFlex FlexColumn">
            {featureDetails ? (
              <>
                {Object.entries(featureDetails).map(
                  ([featureKey, featureValue]) => {
                    const parsedLayer = LAYER_DETAILS[featureKey];
                    if (parsedLayer) {
                      const { key, parser } = parsedLayer;
                      return (
                        <DataItem
                          key={`FEATURE_DETAIL_${featureKey}`}
                          left={key ? key : basicStringParse(featureKey)}
                          middle={
                            parser
                              ? parser(featureValue)
                              : basicStringParse(featureValue)
                          }
                        />
                      );
                    }
                    return null;
                  }
                )}
                <h5 className="ImportantSubText NoDetailsMessage">
                  There are no layer details at this time
                </h5>
              </>
            ) : (
              <h5 className="ImportantSubText">
                There are no layer details at this time
              </h5>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  // Small devices will open a bottom panel
  // SEE: FEATURE_LAYER_FULLSCREEN
  if (!largerThanMobileView) {
    return (
      <Panel
        showPanel={targetFeature !== null}
        direction="bottom"
        headerBorder="BorderBottom"
        closePanel={closePanel}
        underlayClass="NoUnderlay"
        panelSize="full"
        // scrollClass="ScrollAllInner"
        scrollClass="ScrollContent"
        headerElement={<h2 className="h2Small">{titleText}</h2>}
      >
        {featureLayerInner}
      </Panel>
    );
  } // NOTE: Only return null here if the `targetFeature` is `null`
  // If `featureDetails` is `undefined`, we will show placeholder UI
  //
  // NOTE: This is here (and not above Panel) to allow it to control it's own
  // mount/unmount animations
  if (targetFeature === null) {
    return null;
  }
  // Normal devices will open the Feature Layer in a portal so it moves inside krpano
  return (
    <PortalledContent targetPortal={PORTAL_ID} children={featureLayerInner} />
  );
};

export default FeatureLayer;
